import { useState, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import Header from "@/components/Header";
import { 
  FileText, 
  Upload, 
  Briefcase, 
  ArrowRight, 
  CheckCircle, 
  Sparkles,
  Target,
  MessageSquare,
  Clock,
  Link as LinkIcon,
  FileUp,
  AlertCircle,
  RefreshCw,
  XCircle,
  Loader2,
  Lightbulb,
  TrendingUp,
  Users,
  Award
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Axios instance with timeout
const apiClient = axios.create({
  baseURL: API,
  timeout: 180000, // 3 minute timeout for generation
});

// Interview tips and facts for loading animation
const INTERVIEW_TIPS = [
  { icon: Clock, text: "Recruiters decide on candidates within the first 90 seconds of an interview." },
  { icon: TrendingUp, text: "Using the STAR method increases your success rate by up to 60%." },
  { icon: Users, text: "78% of hiring managers value cultural fit as much as skills." },
  { icon: Award, text: "Candidates who research the company are 2x more likely to get an offer." },
  { icon: Lightbulb, text: "Asking thoughtful questions at the end can boost your chances by 30%." },
  { icon: MessageSquare, text: "Clear, concise answers are preferred over jargon-heavy responses." },
  { icon: Target, text: "Quantifying achievements makes your answers 40% more memorable." },
  { icon: Briefcase, text: "Practice makes perfect — rehearsing answers reduces anxiety by 50%." },
  { icon: CheckCircle, text: "Following up with a thank-you email increases callback rates by 25%." },
  { icon: TrendingUp, text: "Demonstrating enthusiasm for the role is the #1 factor in hiring decisions." },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [cvFile, setCvFile] = useState(null);
  const [cvData, setCvData] = useState(null);
  const [jobInputType, setJobInputType] = useState("url");
  const [jobUrl, setJobUrl] = useState("");
  const [jobText, setJobText] = useState("");
  const [jobData, setJobData] = useState(null);
  const [interviewType, setInterviewType] = useState("general");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState(null);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);

  // Rotate tips every 4 seconds during loading
  useEffect(() => {
    let interval;
    if (isLoading) {
      interval = setInterval(() => {
        setCurrentTipIndex((prev) => (prev + 1) % INTERVIEW_TIPS.length);
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  const clearError = () => setError(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setCvFile(file);
    setIsLoading(true);
    setLoadingStep("Parsing your CV...");
    setLoadingProgress(20);
    clearError();

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await apiClient.post("/upload-cv", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000, // 30s timeout for CV upload
      });
      
      const data = response.data;
      
      // Check for CV validation warning
      if (data.warning_message) {
        if (!data.is_valid_cv) {
          // Document doesn't look like a CV - show warning and ask for confirmation
          toast.warning(data.warning_message, { duration: 8000 });
          const proceed = window.confirm(
            `${data.warning_message}\n\nDo you want to continue anyway, or would you like to upload a different file?`
          );
          if (!proceed) {
            setCvFile(null);
            setIsLoading(false);
            setLoadingStep("");
            setLoadingProgress(0);
            return;
          }
        } else {
          // CV is valid but incomplete - just show info toast
          toast.info(data.warning_message, { duration: 5000 });
        }
      }
      
      setCvData(data);
      setLoadingProgress(100);
      toast.success("CV parsed successfully!");
      setStep(2);
    } catch (error) {
      console.error("CV upload error:", error);
      const errorMsg = error.response?.data?.detail || 
                       error.message === "timeout of 30000ms exceeded" ? "Upload timed out. Please try again." :
                       "Failed to parse CV. Please try a different file.";
      setError({ type: "cv", message: errorMsg });
      toast.error(errorMsg);
      setCvFile(null);
    } finally {
      setIsLoading(false);
      setLoadingStep("");
      setLoadingProgress(0);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"]
    },
    maxFiles: 1,
    disabled: isLoading
  });

  const handleJobSubmit = async () => {
    if (jobInputType === "url" && !jobUrl) {
      toast.error("Please enter a job URL");
      return;
    }
    if (jobInputType === "text" && !jobText) {
      toast.error("Please paste the job description");
      return;
    }

    setIsLoading(true);
    setLoadingStep("Analyzing job description...");
    setLoadingProgress(30);
    clearError();

    try {
      const response = await apiClient.post("/scrape-job", {
        url: jobInputType === "url" ? jobUrl : null,
        manual_text: jobInputType === "text" ? jobText : null
      }, {
        timeout: 20000, // 20s timeout for job scraping
      });
      setJobData(response.data);
      setLoadingProgress(100);
      toast.success("Job description analyzed!");
      setStep(3);
    } catch (error) {
      console.error("Job scrape error:", error);
      let errorMsg = "Failed to analyze job description.";
      
      if (error.response?.status === 408) {
        errorMsg = "Job URL request timed out. Please paste the description manually.";
        setJobInputType("text");
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.code === "ECONNABORTED") {
        errorMsg = "Request timed out. Please try pasting the job description manually.";
        setJobInputType("text");
      }
      
      setError({ type: "job", message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
      setLoadingStep("");
      setLoadingProgress(0);
    }
  };

  // ACTUAL GENERATION - No login required, generate immediately
  const handleGenerate = async () => {
    if (!cvData || !jobData) {
      toast.error("Please complete all steps first");
      return;
    }

    setIsLoading(true);
    setLoadingStep("Generating your personalized interview prep...");
    setLoadingProgress(10);
    clearError();

    try {
      // Generate interview prep directly without authentication
      const response = await apiClient.post("/generate-anonymous", {
        cv_text: cvData.text,
        job_description: jobData.description || jobText,
        job_title: jobData.title || "Position",
        company_name: jobData.company || "Company",
        interview_type: interviewType
      }, {
        timeout: 180000, // 3 minute timeout for generation (OCR + AI can take time)
        onDownloadProgress: (progressEvent) => {
          // Update progress based on response
          const progress = Math.min(90, 10 + (progressEvent.loaded / 1000));
          setLoadingProgress(progress);
        }
      });

      setLoadingProgress(100);
      toast.success("Interview prep generated!");

      // Store the generated data for potential saving later
      const generatedData = {
        content: response.data,
        cvData,
        jobData,
        interviewType,
        timestamp: Date.now()
      };
      sessionStorage.setItem('hireun_generated_prep', JSON.stringify(generatedData));

      // Navigate to results page with the generated data
      navigate("/results", { 
        state: { 
          interviewData: response.data,
          jobData,
          cvData,
          interviewType,
          isAnonymous: true // Flag to show save CTA
        }
      });

    } catch (error) {
      console.error("Generation error:", error);
      let errorMsg = "Failed to generate interview prep.";
      
      if (error.code === "ECONNABORTED") {
        errorMsg = "Generation timed out. Please try again.";
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      setError({ type: "generate", message: errorMsg, canRetry: true });
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
      setLoadingStep("");
      setLoadingProgress(0);
    }
  };

  const handleRetry = () => {
    clearError();
    if (error?.type === "generate") {
      handleGenerate();
    }
  };

  const currentTip = INTERVIEW_TIPS[currentTipIndex];
  const TipIcon = currentTip?.icon || Lightbulb;

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Master Your Interview in{" "}
              <span className="text-primary">Minutes</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg text-muted-foreground mb-8"
            >
              Upload your CV. Paste the job offer. Get interview-ready with personalized 
              scripts, questions, and answers tailored to your background.
            </motion.p>
            
            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-4 mb-12">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                    step >= s ? "bg-primary text-white" : "bg-secondary text-muted-foreground"
                  }`}>
                    {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                  </div>
                  {s < 3 && (
                    <div className={`w-16 h-1 mx-2 rounded transition-all ${
                      step > s ? "bg-primary" : "bg-secondary"
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="max-w-2xl mx-auto">
            <AnimatePresence mode="wait">
              {/* Step 1: Upload CV */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <Card className="shadow-lg border-border">
                    <CardContent className="p-8">
                      <div className="text-center mb-6">
                        <FileUp className="w-12 h-12 text-primary mx-auto mb-4" />
                        <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          Upload Your CV
                        </h2>
                        <p className="text-muted-foreground">
                          We'll extract your skills and experience to create personalized answers
                        </p>
                      </div>

                      {/* Error Display */}
                      {error?.type === "cv" && (
                        <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-destructive font-medium">Upload Failed</p>
                            <p className="text-sm text-destructive/80">{error.message}</p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={clearError}>
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      <div
                        {...getRootProps()}
                        data-testid="cv-dropzone"
                        className={`dropzone ${isDragActive ? "dropzone-active" : ""} ${cvFile && !error ? "dropzone-accepted" : ""} ${error?.type === "cv" ? "border-destructive" : ""}`}
                      >
                        <input {...getInputProps()} data-testid="cv-input" />
                        {isLoading ? (
                          <div className="flex flex-col items-center">
                            <Loader2 className="w-12 h-12 text-primary mb-4 animate-spin" />
                            <p className="text-muted-foreground font-medium">{loadingStep}</p>
                            <Progress value={loadingProgress} className="w-48 mt-3" />
                          </div>
                        ) : cvFile && !error ? (
                          <div className="flex flex-col items-center">
                            <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
                            <p className="font-medium">{cvFile.name}</p>
                            <p className="text-sm text-muted-foreground mt-1">Click or drop to replace</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <Upload className="w-12 h-12 text-muted-foreground mb-4" />
                            <p className="font-medium mb-1">
                              {isDragActive ? "Drop your CV here" : "Drag & drop your CV"}
                            </p>
                            <p className="text-sm text-muted-foreground">or click to browse</p>
                            <p className="text-xs text-muted-foreground mt-4">
                              Supports PDF and DOCX files
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Step 2: Job Description */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <Card className="shadow-lg border-border">
                    <CardContent className="p-8">
                      <div className="text-center mb-6">
                        <Briefcase className="w-12 h-12 text-primary mx-auto mb-4" />
                        <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          Add the Job Description
                        </h2>
                        <p className="text-muted-foreground">
                          Paste the job URL or description to tailor your preparation
                        </p>
                      </div>

                      {/* Error Display */}
                      {error?.type === "job" && (
                        <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-destructive font-medium">Analysis Failed</p>
                            <p className="text-sm text-destructive/80">{error.message}</p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={clearError}>
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      <Tabs value={jobInputType} onValueChange={setJobInputType} className="mb-6">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="url" data-testid="job-url-tab">
                            <LinkIcon className="w-4 h-4 mr-2" />
                            Job URL
                          </TabsTrigger>
                          <TabsTrigger value="text" data-testid="job-text-tab">
                            <FileText className="w-4 h-4 mr-2" />
                            Paste Text
                          </TabsTrigger>
                        </TabsList>
                        <TabsContent value="url" className="mt-4">
                          <Input
                            type="url"
                            placeholder="https://linkedin.com/jobs/..."
                            value={jobUrl}
                            onChange={(e) => setJobUrl(e.target.value)}
                            data-testid="job-url-input"
                            className="h-12"
                            disabled={isLoading}
                          />
                          <p className="text-xs text-muted-foreground mt-2">
                            Works with LinkedIn, Indeed, Glassdoor, and company career pages
                          </p>
                        </TabsContent>
                        <TabsContent value="text" className="mt-4">
                          <Textarea
                            placeholder="Paste the full job description here..."
                            value={jobText}
                            onChange={(e) => setJobText(e.target.value)}
                            data-testid="job-text-input"
                            rows={8}
                            disabled={isLoading}
                          />
                        </TabsContent>
                      </Tabs>

                      {/* Loading indicator */}
                      {isLoading && (
                        <div className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Loader2 className="w-4 h-4 animate-spin text-primary" />
                            <span className="text-sm text-muted-foreground">{loadingStep}</span>
                          </div>
                          <Progress value={loadingProgress} />
                        </div>
                      )}

                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          onClick={() => setStep(1)}
                          className="flex-1"
                          disabled={isLoading}
                        >
                          Back
                        </Button>
                        <Button 
                          onClick={handleJobSubmit}
                          disabled={isLoading}
                          data-testid="analyze-job-btn"
                          className="flex-1 bg-primary hover:bg-primary/90"
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              Analyze Job
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Step 3: Generate */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <Card className="shadow-lg border-border">
                    <CardContent className="p-8">
                      <div className="text-center mb-6">
                        <Sparkles className="w-12 h-12 text-primary mx-auto mb-4" />
                        <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          Generate Your Interview Prep
                        </h2>
                        <p className="text-muted-foreground">
                          Choose your interview type and get personalized content
                        </p>
                      </div>

                      {/* Error Display with Retry */}
                      {error?.type === "generate" && (
                        <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                          <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm text-destructive font-medium">Generation Failed</p>
                              <p className="text-sm text-destructive/80">{error.message}</p>
                            </div>
                          </div>
                          {error.canRetry && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={handleRetry}
                              className="mt-3"
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Try Again
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Summary */}
                      <div className="bg-secondary/50 rounded-lg p-4 mb-6">
                        <div className="flex items-start gap-3 mb-3">
                          <FileText className="w-5 h-5 text-primary mt-0.5" />
                          <div>
                            <p className="font-medium text-sm">Your CV</p>
                            <p className="text-xs text-muted-foreground">{cvFile?.name}</p>
                            {cvData?.skills?.length > 0 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Skills: {cvData.skills.slice(0, 5).join(", ")}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Briefcase className="w-5 h-5 text-primary mt-0.5" />
                          <div>
                            <p className="font-medium text-sm">{jobData?.title || "Job Position"}</p>
                            <p className="text-xs text-muted-foreground">{jobData?.company || "Company"}</p>
                          </div>
                        </div>
                      </div>

                      {/* Interview Type Selection */}
                      <div className="mb-6">
                        <p className="text-sm font-medium mb-3">Interview Type</p>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { value: "general", label: "General", icon: MessageSquare },
                            { value: "hr", label: "HR Screen", icon: Target },
                            { value: "technical", label: "Technical", icon: FileText },
                            { value: "manager", label: "Manager", icon: Briefcase }
                          ].map(({ value, label, icon: Icon }) => (
                            <button
                              key={value}
                              onClick={() => setInterviewType(value)}
                              disabled={isLoading}
                              data-testid={`interview-type-${value}`}
                              className={`p-3 rounded-lg border text-left transition-all ${
                                interviewType === value 
                                  ? "border-primary bg-accent text-primary" 
                                  : "border-border hover:border-primary/50"
                              } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                              <Icon className="w-4 h-4 mb-1" />
                              <p className="text-sm font-medium">{label}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          onClick={() => setStep(2)}
                          className="flex-1"
                          disabled={isLoading}
                        >
                          Back
                        </Button>
                        <Button 
                          onClick={handleGenerate}
                          disabled={isLoading}
                          data-testid="generate-btn"
                          className="flex-1 bg-primary hover:bg-primary/90"
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Generate Prep
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Enhanced Loading Overlay with Tips */}
            {isLoading && step === 3 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
              >
                <Card className="w-96 mx-4">
                  <CardContent className="p-8">
                    <div className="text-center">
                      <div className="relative w-16 h-16 mx-auto mb-6">
                        <div className="absolute inset-0 border-4 border-primary/20 rounded-full" />
                        <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                        <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-primary" />
                      </div>
                      
                      <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Creating Your Interview Prep
                      </h3>
                      
                      <p className="text-sm text-muted-foreground mb-4">
                        {loadingStep || "Our AI is analyzing your CV and the job description..."}
                      </p>
                      
                      <Progress value={loadingProgress} className="mb-6" />
                      
                      {/* Rotating Tips */}
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={currentTipIndex}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3 }}
                          className="bg-secondary/50 rounded-lg p-4 text-left"
                        >
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                              <TipIcon className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-primary mb-1">Did you know?</p>
                              <p className="text-sm text-muted-foreground">{currentTip?.text}</p>
                            </div>
                          </div>
                        </motion.div>
                      </AnimatePresence>
                      
                      <div className="flex justify-center gap-1 mt-4">
                        {INTERVIEW_TIPS.slice(0, 5).map((_, i) => (
                          <div 
                            key={i} 
                            className={`w-1.5 h-1.5 rounded-full transition-colors ${
                              currentTipIndex % 5 === i ? "bg-primary" : "bg-secondary"
                            }`} 
                          />
                        ))}
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-4">
                        This may take up to 2 minutes...
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Everything You Need to Ace Your Interview
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Get comprehensive preparation materials tailored specifically to your background and the job you're applying for.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Clock,
                title: "Tailored Scripts",
                description: "Get 2-minute and 5-minute introduction scripts based on your actual experience"
              },
              {
                icon: Target,
                title: "STAR Method Answers",
                description: "Behavioral questions answered using your real achievements in STAR format"
              },
              {
                icon: MessageSquare,
                title: "10-15 Likely Questions",
                description: "Specific interview questions based on the job description with ready answers"
              }
            ].map((feature, i) => (
              <Card key={i} className="card-hover">
                <CardContent className="p-6">
                  <feature.icon className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content: What is Hireun */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            What is Hireun?
          </h2>
          <div className="prose prose-gray max-w-none text-muted-foreground space-y-4">
            <p>
              Hireun is an AI-powered interview preparation platform that transforms your CV and a job description
              into a complete, personalised interview kit — in minutes, not hours. Instead of relying on generic
              advice from blogs and forums, Hireun analyses your unique professional background against the
              specific requirements of the role you are targeting, producing tailored scripts, answers, and
              strategic talking points you can rehearse right away.
            </p>
            <p>
              Every output is grounded in your actual experience. From a polished two-minute self-introduction to
              STAR-method behavioural answers drawn from your own achievements, Hireun ensures you walk into
              every interview with confidence and clarity — whether it is a first-round HR screen, a technical
              deep-dive, or a final panel with senior leadership.
            </p>
          </div>
        </div>
      </section>

      {/* SEO Content: Who is Hireun for */}
      <section className="py-16 bg-secondary/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Who is Hireun For?
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4 text-muted-foreground">
              <p>
                <strong className="text-foreground">Job seekers at every level.</strong> Whether you are a
                recent graduate preparing for your first professional interview or a seasoned executive
                navigating a career transition, Hireun adapts its output to match your seniority. Our
                intelligent routing system automatically detects senior-level titles and switches to an
                executive preparation mode that emphasises strategic narrative, leadership impact, and
                board-level communication.
              </p>
              <p>
                <strong className="text-foreground">Career changers.</strong> If you are pivoting industries
                or functions, Hireun identifies the transferable skills on your CV and maps them to the
                language of the target role, helping you articulate your value proposition in terms the
                hiring manager will immediately recognise.
              </p>
            </div>
            <div className="space-y-4 text-muted-foreground">
              <p>
                <strong className="text-foreground">International professionals.</strong> Preparing for an
                interview in a language that is not your first? Hireun generates prep materials in the
                language of the job description, so you can rehearse naturally and build fluency with
                role-specific vocabulary before the big day.
              </p>
              <p>
                <strong className="text-foreground">Time-pressed candidates.</strong> Juggling multiple
                applications and a full-time role leaves little room for traditional interview prep.
                Hireun delivers a comprehensive kit in under three minutes, so you can prepare effectively
                even the evening before a call.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content: Why Choose Hireun */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why Choose Hireun Over Generic Interview Tips?
          </h2>
          <div className="prose prose-gray max-w-none text-muted-foreground space-y-4">
            <p>
              Most interview advice is one-size-fits-all. Blog posts give you template answers; coaching sessions
              cost hundreds of euros; and ChatGPT does not know anything about your career history. Hireun sits
              at the intersection of all three: it provides personalised, expert-quality preparation at a fraction
              of the cost and time.
            </p>
            <p>
              The platform cross-references your skills, experiences, and achievements with the specific
              requirements, responsibilities, and culture signals in the job posting. The result is not a list of
              generic questions — it is a battle-tested interview script that sounds like you, speaks to the role,
              and gives the interviewer exactly the evidence they need to move you to the next round.
            </p>
            <p>
              For senior and executive roles, Hireun goes further. Our Company &amp; Sector Intelligence module
              surfaces strategic signals — recent news, competitive dynamics, industry trends, and networking
              leads — so you can demonstrate domain expertise and commercial awareness from the very first question.
            </p>
          </div>
        </div>
      </section>

      {/* SEO Content: How it works in detail */}
      <section className="py-16 bg-secondary/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            How Hireun Works — Step by Step
          </h2>
          <div className="prose prose-gray max-w-none text-muted-foreground space-y-4">
            <p>
              <strong className="text-foreground">1. Upload your CV.</strong> Drag and drop a PDF or DOCX file.
              Hireun uses advanced OCR and text extraction to parse your skills, job titles, companies, and
              key achievements — even from visually formatted CVs.
            </p>
            <p>
              <strong className="text-foreground">2. Add the job description.</strong> Paste a URL from LinkedIn,
              Indeed, Glassdoor, or any career page, or copy-paste the text directly. Our AI extracts the title,
              company, responsibilities, and required qualifications automatically.
            </p>
            <p>
              <strong className="text-foreground">3. Generate your prep.</strong> In under three minutes, Hireun
              produces a full interview kit: self-introduction scripts, "Tell me about yourself" narratives,
              "Why this company?" answers, 10-15 likely interview questions with tailored responses, and
              behavioural STAR answers drawn from your own experience. Every section can be further refined
              through an interactive AI chat — ask for more detail, a different tone, or industry-specific
              adjustments.
            </p>
            <p>
              <strong className="text-foreground">4. Save and iterate.</strong> Create a free account to save
              your sessions, revisit past preps, and generate new ones for different roles. Premium members
              enjoy unlimited generations, priority processing, and access to Company Intelligence insights.
            </p>
          </div>
        </div>
      </section>

      {/* SEO Links Section */}
      <section className="py-16 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Interview Preparation by Role
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              "Software Engineer",
              "Data Analyst",
              "Product Manager",
              "Consultant",
              "Marketing Manager",
              "Sales Representative",
              "Financial Analyst",
              "Project Manager",
              "UX Designer",
              "HR Manager"
            ].map((role) => (
              <Link
                key={role}
                to={`/interview-questions/${role.toLowerCase().replace(/ /g, "-")}`}
                className="p-4 bg-white rounded-lg border border-border hover:border-primary/50 transition-colors text-center"
              >
                <span className="text-sm font-medium">{role}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Briefcase className="w-5 h-5" />
                <span className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Hireun
                </span>
              </div>
              <p className="text-sm text-gray-400">
                AI-powered interview preparation tailored to your unique background.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/tell-me-about-yourself-interview" className="hover:text-white transition-colors">Tell Me About Yourself</Link></li>
                <li><Link to="/how-to-present-your-experience" className="hover:text-white transition-colors">Present Your Experience</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">By Role</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/interview-questions/software-engineer" className="hover:text-white transition-colors">Software Engineer</Link></li>
                <li><Link to="/interview-questions/product-manager" className="hover:text-white transition-colors">Product Manager</Link></li>
                <li><Link to="/interview-questions/data-analyst" className="hover:text-white transition-colors">Data Analyst</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/privacy" className="cursor-pointer hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="cursor-pointer hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 Hireun. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
